import { useCallback, useEffect, useRef, useState } from 'react'
import { API_ROUTES, type MatchDetail } from '@brochure/shared'
import { apiFetch } from '@/lib/api'
import { useLive } from './useLive'

const RELOAD_ON = new Set(['match', 'event', 'timer', 'tournament'])

/** Partita con dati live: rilettura via `GET /tournaments/:id/matches/:matchId`, aggiornata dai messaggi WebSocket. */
export function useMatch(tournamentId: string | undefined, matchId: string | undefined) {
  const key = tournamentId && matchId ? `${tournamentId}/${matchId}` : null
  const [state, setState] = useState<{ key: string | null; data?: MatchDetail; error?: unknown }>({ key: null })
  const controllerRef = useRef<AbortController | null>(null)

  const reload = useCallback(() => {
    if (!tournamentId || !matchId) return
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    apiFetch<MatchDetail>(`${API_ROUTES.tournaments}/${tournamentId}/matches/${matchId}`, { signal: controller.signal })
      .then((data) => setState({ key, data }))
      .catch((error: unknown) => { if (!controller.signal.aborted) setState({ key, error }) })
  }, [tournamentId, matchId, key])

  useEffect(() => {
    reload()
    return () => controllerRef.current?.abort()
  }, [reload])

  const { isOpen } = useLive(tournamentId, (message) => {
    if (!RELOAD_ON.has(message.type)) return
    // Un evento con matchId riguarda un'altra partita: ignoralo, il torneo può averne diverse in corso.
    if (message.matchId && message.matchId !== matchId) return
    reload()
  })

  const isCurrent = state.key === key
  return {
    data: isCurrent ? state.data : undefined,
    error: isCurrent ? state.error : undefined,
    isLoading: key !== null && !isCurrent,
    reload,
    isLive: isOpen,
  }
}
